import { Check, Edit2, GripVertical, Link as LinkIcon, Trash2, X } from 'lucide-react';
import React, { useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useThemeSettings } from '../../hooks/useThemeSettings';
import { useVideoSystem } from '../../hooks/useVideoSystem';

interface EditingVideo {
  id: string;
  title: string;
  description: string;
  url: string;
}

export function VideoList() {
  const { chatbotPublicId } = useChatbot();
  const { videos, deleteVideo, reorderVideos, updateVideo, loading } = useVideoSystem();
  const { colors } = useDesignColors();
  const { theme } = useThemeSettings();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [editingVideo, setEditingVideo] = useState<EditingVideo | null>(null);
  const isDark = theme.mode === 'dark';

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = async (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index || !chatbotPublicId) return;

    const newVideos = [...videos];
    const draggedVideo = newVideos[draggedIndex];
    newVideos.splice(draggedIndex, 1);
    newVideos.splice(index, 0, draggedVideo);

    await reorderVideos(chatbotPublicId, newVideos.map(v => v.id));
    setDraggedIndex(index);
  };

  const handleDelete = async (videoId: string) => {
    if (!chatbotPublicId) return;
    await deleteVideo(chatbotPublicId, videoId);
  };

  const handleEdit = (video: any) => {
    setEditingVideo({
      id: video.id,
      title: video.title,
      description: video.description || '',
      url: video.url
    });
  };

  const handleSave = async () => {
    if (!editingVideo || !chatbotPublicId) return;

    try {
      await updateVideo(chatbotPublicId, editingVideo.id, {
        title: editingVideo.title,
        description: editingVideo.description,
        url: editingVideo.url,
        type: editingVideo.url.includes('youtube.com') ? 'youtube' :
              editingVideo.url.includes('vimeo.com') ? 'vimeo' :
              editingVideo.url.includes('dailymotion.com') ? 'dailymotion' : 'upload'
      });
      setEditingVideo(null);
    } catch (err) {
      console.error('Error updating video:', err);
    }
  };

  if (videos.length === 0) {
    return (
      <div className="text-center py-12">
        <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>No video</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {videos.map((video, index) => (
        <div
          key={video.id}
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragEnd={() => setDraggedIndex(null)}
          className={`group relative ${isDark ? 'bg-white/5' : 'bg-white'} rounded-lg overflow-hidden border ${
            isDark ? 'border-white/10' : 'border-gray-100'
          } ${loading.delete || loading.reorder ? 'opacity-50' : ''}`}
        >
          <div className="flex gap-4 p-4">
            <div className="w-48 aspect-video rounded-lg overflow-hidden bg-black/20">
              {video.thumbnailUrl && (
                <img
                  src={video.thumbnailUrl}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            
            <div className="flex-1">
              {editingVideo?.id === video.id ? (
                <div className="space-y-3">
                  <input
                    type="text"
                    value={editingVideo.title}
                    onChange={(e) => setEditingVideo(prev => prev ? { ...prev, title: e.target.value } : null)}
                    className={`w-full px-3 py-2 rounded-lg ${
                      isDark 
                        ? 'bg-black/20 border-white/10 text-white' 
                        : 'bg-gray-50 border-gray-200 text-gray-900'
                    } border focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    placeholder="Titre de la vidéo"
                  />
                  <textarea
                    value={editingVideo.description}
                    onChange={(e) => setEditingVideo(prev => prev ? { ...prev, description: e.target.value } : null)}
                    className={`w-full px-3 py-2 rounded-lg ${
                      isDark 
                        ? 'bg-black/20 border-white/10 text-white' 
                        : 'bg-gray-50 border-gray-200 text-gray-900'
                    } border focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                    placeholder="Description de la vidéo"
                    rows={3}
                  />
                  <div className="flex items-center gap-2">
                    <LinkIcon className="w-5 h-5 text-gray-400" />
                    <input
                      type="url"
                      value={editingVideo.url}
                      onChange={(e) => setEditingVideo(prev => prev ? { ...prev, url: e.target.value } : null)}
                      className={`flex-1 px-3 py-2 rounded-lg ${
                        isDark 
                          ? 'bg-black/20 border-white/10 text-white' 
                          : 'bg-gray-50 border-gray-200 text-gray-900'
                      } border focus:outline-none focus:ring-2 focus:ring-indigo-500`}
                      placeholder="URL de la vidéo"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setEditingVideo(null)}
                      className={`p-2 rounded-lg ${
                        isDark 
                          ? 'bg-white/5 text-gray-300 hover:bg-white/10' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      } transition-colors`}
                    >
                      <X className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleSave}
                      className="p-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h4 
                    className="text-lg font-medium mb-2 bg-clip-text text-transparent bg-gradient-to-r"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`
                    }}
                  >
                    {video.title}
                  </h4>
                  {video.description && (
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                      {video.description}
                    </p>
                  )}
                  <div className="mt-2 flex items-center gap-2 text-sm text-gray-400">
                    <LinkIcon className="w-4 h-4" />
                    <a 
                      href={video.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-indigo-400 transition-colors truncate"
                    >
                      {video.url}
                    </a>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handleEdit(video)}
                className={`p-2 rounded-lg ${
                  isDark 
                    ? 'bg-white/5 text-gray-300 hover:bg-white/10' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } transition-colors`}
              >
                <Edit2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => handleDelete(video.id)}
                className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
              >
                <Trash2 className="w-5 h-5" />
              </button>
              <div className={`p-2 cursor-move ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <GripVertical className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}